const PROJECT_ID = process.env.REACT_APP_POSTHOG_PROJECT_ID;
const API_KEY = process.env.REACT_APP_POSTHOG_PERSONAL_API_KEY;

async function hogqlQuery(query) {
  if (!PROJECT_ID || !API_KEY) {
    console.warn('PostHog API keys not configured');
    return { results: [], columns: [] };
  }

  try {
    const res = await fetch(`/ingest/api/projects/${PROJECT_ID}/query/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${API_KEY}`,
      },
      body: JSON.stringify({ query: { kind: 'HogQLQuery', query } }),
    });

    if (!res.ok) {
      console.error('PostHog query failed:', res.status);
      return { results: [], columns: [] };
    }

    return res.json();
  } catch (err) {
    console.error('PostHog query error:', err.message);
    return { results: [], columns: [] };
  }
}

// Date range helpers
function getDateRange(rangeKey) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (rangeKey) {
    case 'today': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        from: today.toISOString(),
        to: now.toISOString(),
        prevFrom: yesterday.toISOString(),
        prevTo: today.toISOString(),
        interval: 'hour',
      };
    }
    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const dayBefore = new Date(today);
      dayBefore.setDate(dayBefore.getDate() - 2);
      return {
        from: yesterday.toISOString(),
        to: today.toISOString(),
        prevFrom: dayBefore.toISOString(),
        prevTo: yesterday.toISOString(),
        interval: 'hour',
      };
    }
    case '7d': {
      const d7 = new Date(today);
      d7.setDate(d7.getDate() - 7);
      const d14 = new Date(today);
      d14.setDate(d14.getDate() - 14);
      return {
        from: d7.toISOString(),
        to: now.toISOString(),
        prevFrom: d14.toISOString(),
        prevTo: d7.toISOString(),
        interval: 'day',
      };
    }
    case '30d': {
      const d30 = new Date(today);
      d30.setDate(d30.getDate() - 30);
      const d60 = new Date(today);
      d60.setDate(d60.getDate() - 60);
      return {
        from: d30.toISOString(),
        to: now.toISOString(),
        prevFrom: d60.toISOString(),
        prevTo: d30.toISOString(),
        interval: 'day',
      };
    }
    case 'month': {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        from: monthStart.toISOString(),
        to: now.toISOString(),
        prevFrom: prevMonthStart.toISOString(),
        prevTo: monthStart.toISOString(),
        interval: 'day',
      };
    }
    case 'all': {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      const twoYearsAgo = new Date(today);
      twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
      return {
        from: yearAgo.toISOString(),
        to: now.toISOString(),
        prevFrom: twoYearsAgo.toISOString(),
        prevTo: yearAgo.toISOString(),
        interval: 'month',
      };
    }
    default:
      return getDateRange('7d');
  }
}

export async function getOverviewStats(rangeKey) {
  const { from, to, prevFrom, prevTo } = getDateRange(rangeKey);

  const [current, previous] = await Promise.all([
    hogqlQuery(`
      SELECT
        count(DISTINCT distinct_id) as visitors,
        count() as pageviews
      FROM events
      WHERE event = '$pageview'
        AND timestamp >= '${from}'
        AND timestamp <= '${to}'
    `),
    hogqlQuery(`
      SELECT
        count(DISTINCT distinct_id) as visitors,
        count() as pageviews
      FROM events
      WHERE event = '$pageview'
        AND timestamp >= '${prevFrom}'
        AND timestamp <= '${prevTo}'
    `),
  ]);

  const visitors = current.results?.[0]?.[0] || 0;
  const pageviews = current.results?.[0]?.[1] || 0;
  const prevVisitors = previous.results?.[0]?.[0] || 0;
  const prevPageviews = previous.results?.[0]?.[1] || 0;

  const viewsPerVisit = visitors > 0 ? (pageviews / visitors).toFixed(1) : '0';

  const visitorsChange = prevVisitors > 0
    ? Math.round(((visitors - prevVisitors) / prevVisitors) * 100)
    : 0;
  const pageviewsChange = prevPageviews > 0
    ? Math.round(((pageviews - prevPageviews) / prevPageviews) * 100)
    : 0;

  return {
    visitors: { value: visitors, change: visitorsChange },
    pageviews: { value: pageviews, change: pageviewsChange },
    viewsPerVisit: { value: viewsPerVisit, change: null },
  };
}

export async function getCurrentVisitors() {
  const data = await hogqlQuery(`
    SELECT count(DISTINCT distinct_id) as visitors
    FROM events
    WHERE event = '$pageview'
      AND timestamp >= now() - INTERVAL 5 MINUTE
  `);
  return data.results?.[0]?.[0] || 0;
}

export async function getVisitorTrend(rangeKey) {
  const { from, to, interval } = getDateRange(rangeKey);
  const bucket = interval === 'hour' ? 'toStartOfHour' : interval === 'month' ? 'toStartOfMonth' : 'toStartOfDay';

  const data = await hogqlQuery(`
    SELECT
      ${bucket}(timestamp) as period,
      count(DISTINCT distinct_id) as visitors
    FROM events
    WHERE event = '$pageview'
      AND timestamp >= '${from}'
      AND timestamp <= '${to}'
    GROUP BY period
    ORDER BY period
  `);

  return {
    data: (data.results || []).map(([period, visitors]) => ({
      period: new Date(period).toISOString(),
      visitors,
    })),
    interval,
  };
}

export async function getTopPages(rangeKey, limit = 10) {
  const { from, to } = getDateRange(rangeKey);

  const data = await hogqlQuery(`
    SELECT
      properties.$pathname as page,
      count(DISTINCT distinct_id) as visitors
    FROM events
    WHERE event = '$pageview'
      AND timestamp >= '${from}'
      AND timestamp <= '${to}'
      AND properties.$pathname != ''
    GROUP BY page
    ORDER BY visitors DESC
    LIMIT ${limit}
  `);

  return (data.results || []).map(([page, visitors]) => ({ page, visitors }));
}

export async function getSources(rangeKey, limit = 10) {
  const { from, to } = getDateRange(rangeKey);

  const data = await hogqlQuery(`
    SELECT
      if(properties.$referring_domain = '' OR properties.$referring_domain IS NULL, 'Direkte / Ingen', properties.$referring_domain) as source,
      count(DISTINCT distinct_id) as visitors
    FROM events
    WHERE event = '$pageview'
      AND timestamp >= '${from}'
      AND timestamp <= '${to}'
    GROUP BY source
    ORDER BY visitors DESC
    LIMIT ${limit}
  `);

  return (data.results || []).map(([source, visitors]) => ({ source, visitors }));
}

export async function getBrowsers(rangeKey) {
  const { from, to } = getDateRange(rangeKey);

  const data = await hogqlQuery(`
    SELECT
      if(properties.$browser = '' OR properties.$browser IS NULL, 'Ukjent', properties.$browser) as browser,
      count(DISTINCT distinct_id) as visitors
    FROM events
    WHERE event = '$pageview'
      AND timestamp >= '${from}'
      AND timestamp <= '${to}'
    GROUP BY browser
    ORDER BY visitors DESC
    LIMIT 8
  `);

  return (data.results || []).map(([browser, visitors]) => ({ name: browser, visitors }));
}

export async function getDevices(rangeKey) {
  const { from, to } = getDateRange(rangeKey);

  const data = await hogqlQuery(`
    SELECT
      if(properties.$device_type = '' OR properties.$device_type IS NULL, 'Ukjent', properties.$device_type) as device,
      count(DISTINCT distinct_id) as visitors
    FROM events
    WHERE event = '$pageview'
      AND timestamp >= '${from}'
      AND timestamp <= '${to}'
    GROUP BY device
    ORDER BY visitors DESC
    LIMIT 5
  `);

  const deviceLabels = { Desktop: 'Desktop', Mobile: 'Mobil', Tablet: 'Nettbrett' };

  return (data.results || []).map(([device, visitors]) => ({
    name: deviceLabels[device] || device,
    visitors,
  }));
}

export async function getTopEvents(rangeKey) {
  const { from, to } = getDateRange(rangeKey);

  const data = await hogqlQuery(`
    SELECT
      event,
      count() as count
    FROM events
    WHERE timestamp >= '${from}'
      AND timestamp <= '${to}'
      AND event NOT LIKE '$%'
    GROUP BY event
    ORDER BY count DESC
    LIMIT 10
  `);

  return (data.results || []).map(([event, count]) => ({ event, count }));
}
